/* ==========================================================================
   Custom theme JS — Express Food Service
   Loaded after theme.js. Nothing here should be duplicated in theme.js.
   ========================================================================== */

(function () {
  'use strict';

  /* Selector for the product-card add-to-cart forms (grid + list view) */
  const CARD_FORMS = '.product-card__inline-actions, .product-card__list-actions-main';

  /* ------------------------------------------------------------------
     1. Product card quantity selector (+ / −)
     Delegated, so it survives infinite scroll, filtering and lazy loads.
     ------------------------------------------------------------------ */
  (function quantitySelector() {
    if (window.__pcardQtyInit) return;
    window.__pcardQtyInit = true;

    document.addEventListener('click', function (event) {
      const btn = event.target.closest('[data-qty-minus], [data-qty-plus]');
      if (!btn) return;
      const wrapper = btn.closest('.product-card__qty');
      if (!wrapper) return;
      const input = wrapper.querySelector('input[name="quantity"]');
      if (!input) return;

      event.preventDefault();
      event.stopPropagation();

      const min = parseInt(input.min) || 1;
      const step = btn.hasAttribute('data-qty-plus') ? 1 : -1;
      input.value = Math.max(min, (parseInt(input.value) || min) + step);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  })();

  /* ------------------------------------------------------------------
     2. Suppress the cart drawer for product-card quick adds
     Patches CartDrawer.prototype.show rather than editing theme.js,
     so a theme update won't undo it.
     ------------------------------------------------------------------ */
  (function suppressDrawer() {
    if (window.__pcardQuietAddInit) return;
    window.__pcardQuietAddInit = true;

    let quiet = false;
    let quietTimer = null;

    // Flag card submits in the capture phase, before ProductForm handles them
    document.addEventListener(
      'submit',
      function (event) {
        const form = event.target;
        if (!form.matches || !form.matches(CARD_FORMS)) return;
        quiet = true;
        clearTimeout(quietTimer);
        quietTimer = setTimeout(function () {
          quiet = false;
        }, 4000);
      },
      true
    );

    function patch() {
      const CartDrawer = window.customElements.get('cart-drawer');
      if (!CartDrawer || CartDrawer.prototype.__quietPatched) return false;

      const originalShow = CartDrawer.prototype.show;
      CartDrawer.prototype.show = function () {
        if (quiet) {
          quiet = false;
          clearTimeout(quietTimer);
          return;
        }
        return originalShow.apply(this, arguments);
      };
      CartDrawer.prototype.__quietPatched = true;
      return true;
    }

    // cart.js may define cart-drawer after this file parses
    if (!patch()) {
      let attempts = 0;
      const poll = setInterval(function () {
        if (patch() || ++attempts > 40) clearInterval(poll);
      }, 50);
    }
  })();

  /* ------------------------------------------------------------------
     3. "Added" button feedback on product cards
     product-ajax:added is dispatched by ProductForm on a successful add.
     ------------------------------------------------------------------ */
  (function addedFeedback() {
    if (window.__pcardAddedFeedbackInit) return;
    window.__pcardAddedFeedbackInit = true;

    const ADDED_MS = 1800;
    const ADDED_TEXT = 'Added';

    document.addEventListener(
      'submit',
      function (event) {
        const form = event.target;
        if (!form.matches || !form.matches(CARD_FORMS)) return;

        const onAdded = function () {
          cleanup();
          flash(form);
        };
        const cleanup = function () {
          document.removeEventListener('product-ajax:added', onAdded);
          document.removeEventListener('product-ajax:error', cleanup);
          clearTimeout(timer);
        };

        document.addEventListener('product-ajax:added', onAdded);
        document.addEventListener('product-ajax:error', cleanup);
        const timer = setTimeout(cleanup, 6000);
      },
      true
    );

    function flash(form) {
      const btn = form.querySelector('button[name="add"]');
      if (!btn || btn.dataset.addedPending === 'true') return;
      const label = btn.querySelector('.product-card__atc-text') || btn.querySelector('.btn__text');
      if (!label) return;

      btn.dataset.addedPending = 'true';
      const original = label.textContent;
      label.textContent = ADDED_TEXT;
      btn.classList.add('is-added');

      setTimeout(function () {
        label.textContent = original;
        btn.classList.remove('is-added');
        btn.dataset.addedPending = 'false';
      }, ADDED_MS);
    }
  })();

  /* ------------------------------------------------------------------
     4. Quick order accordions
     Lazy-loads each category's products on first open.
     ------------------------------------------------------------------ */
  (function quickOrder() {
    if (window.__quickOrderInit) return;
    window.__quickOrderInit = true;

    function extractSection(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector('.quick-order__products') || doc.body;
    }

    async function loadPanel(details) {
      if (details.dataset.qoLoaded === 'true' || details.dataset.qoLoading === 'true') return;
      const panel = details.querySelector('[data-qo-panel]');
      const url = details.dataset.qoUrl;
      if (!panel || !url) return;

      details.dataset.qoLoading = 'true';
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(res.status);
        panel.innerHTML = '';
        panel.appendChild(extractSection(await res.text()));
        details.dataset.qoLoaded = 'true';
      } catch (err) {
        panel.innerHTML = '<p class="quick-order__no-products">Could not load products. Please refresh.</p>';
        console.error('Quick order load failed:', err);
      } finally {
        details.dataset.qoLoading = 'false';
      }
    }

    // 'toggle' does not bubble — listen in the capture phase
    document.addEventListener(
      'toggle',
      function (event) {
        const details = event.target;
        if (details.matches && details.matches('[data-qo-item]') && details.open) {
          loadPanel(details);
        }
      },
      true
    );

    function loadOpenPanels() {
      document.querySelectorAll('[data-qo-item][open]').forEach(loadPanel);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', loadOpenPanels);
    } else {
      loadOpenPanels();
    }

    document.addEventListener('shopify:section:load', function (event) {
      if (event.target.querySelector('[data-qo-item]')) loadOpenPanels();
    });

    // Load more, delegated
    document.addEventListener('click', async function (event) {
      const btn = event.target.closest('[data-qo-load-more]');
      if (!btn) return;
      event.preventDefault();
      if (btn.dataset.loading === 'true') return;

      btn.dataset.loading = 'true';
      btn.classList.add('is-loading');

      try {
        const res = await fetch(btn.href);
        if (!res.ok) throw new Error(res.status);
        const incoming = extractSection(await res.text());
        const container = btn.closest('.quick-order__products');
        const grid = container ? container.querySelector('[data-qo-grid]') : null;
        const newGrid = incoming.querySelector('[data-qo-grid]');
        if (grid && newGrid) grid.append(...newGrid.children);

        const newBtn = incoming.querySelector('[data-qo-load-more]');
        const wrap = btn.closest('[data-qo-load-more-wrap]');
        if (newBtn) {
          btn.href = newBtn.href;
        } else if (wrap) {
          wrap.remove();
        }
      } catch (err) {
        console.error('Quick order load more failed:', err);
      } finally {
        btn.dataset.loading = 'false';
        btn.classList.remove('is-loading');
      }
    });

    // Category filter
    document.addEventListener('input', function (event) {
      const input = event.target.closest('[data-qo-search]');
      if (!input) return;
      const term = input.value.trim().toLowerCase();
      const list = document.querySelector('[data-qo-list]');
      const empty = document.querySelector('[data-qo-empty]');
      if (!list) return;

      let visible = 0;
      list.querySelectorAll('[data-qo-item]').forEach(function (item) {
        const match = !term || (item.dataset.qoTitle || '').includes(term);
        item.hidden = !match;
        if (match) visible++;
      });
      if (empty) empty.classList.toggle('hidden', visible > 0);
    });
  })();
})();